import {
  mergeAttributes,
  Node,
  type Attribute,
  type JSONContent,
  type MarkdownRendererHelpers,
} from "@tiptap/core"
import * as z from "zod"
import { CARD_COLORS } from "../../util/colors"
import { formatMarkdownAttributes } from "../../util/markdown-json-attributes"
import { childrenWithLineBreaks, md, nodePlainText } from "../../util/markdown-template"
import { objectId } from "../../util/object-id"
import { toTiptapAttributes } from "../../util/render-data"
import {
  ExtraPostPropertiesSchema,
  GoogleClassroomPostTypeSchema,
} from "../card-google-classroom/google-classroom-attrs"
import { CardTitleAttrsSchema } from "../card-title/card-title-content-extension"

export const CardAttrsSchema = z.object({
  id: z.string(),
  parentCardId: z.string().nullable().default(null),
  commentIds: z.array(z.string()).default([]),
  isPublic: z.boolean().default(false),
})
export type CardExtensionAttrs = z.infer<typeof CardAttrsSchema>

export const CARD_ID_ATTRIBUTE = {
  // If the id is not present, create a new one.
  parseHTML: (element) => element.getAttribute("data-id") || objectId(),
} satisfies Partial<Attribute>

/**
 * Schema for card attributes in Markdown, which appear as a JSON object at the
 * end of the title (H2) line.
 *
 * These attributes are distinct from the actual card attributes (CardExtensionAttrs):
 *
 * - id is optional in parsed Markdown; we create it if needed in card-stack-markdown.ts
 * - Includes cardTitle attrs (color)
 * - Shared between titledCard, untitledCard, and googleClassroomCard,
 * with booleans to distinguish between them
 * - The googleClassroom-only post fields are optional, so a card that has no
 * value for them writes no key.
 * Defaults are filled in in card-stack-markdown.ts for actual GC Cards.
 * - Every field .catches, so a malformed one takes its default instead of
 * failing the whole trailer.
 */
export const CardMarkdownAttrsSchema = z.object({
  id: z.string().optional().catch(undefined),
  color: z.enum(CARD_COLORS).optional().catch(undefined),
  isPublic: z.boolean().default(false).catch(false),
  isUntitled: z.boolean().default(false).catch(false),
  googleClassroom: z.boolean().default(false).catch(false),
  postType: GoogleClassroomPostTypeSchema.optional().catch(undefined),
  maxPoints: z.string().nullish().catch(undefined),
  revision: z.number().optional().catch(undefined),
  extraPostProperties: ExtraPostPropertiesSchema.optional().catch(undefined),
})

export const CARD_VALUE_SCHEMA = "block+"

export const TitledCardContentExtension = Node.create({
  name: "titledCard",
  group: "card addTarget",
  content: "cardTitle " + CARD_VALUE_SCHEMA,
  draggable: true,

  addAttributes: () => toTiptapAttributes(CardAttrsSchema, { id: CARD_ID_ATTRIBUTE }),
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { "data-content-type": "titledCard" }),
    0,
  ],
  parseHTML: () => [{ tag: 'div[data-content-type="titledCard"]' }],

  renderMarkdown(node: JSONContent, helpers: MarkdownRendererHelpers): string {
    const card = CardAttrsSchema.parse(node.attrs)
    const [titleNode, ...blocks] = node.content || []
    const titleAttrs = CardTitleAttrsSchema.parse(titleNode?.attrs ?? {})
    const heading = nodePlainText(titleNode)
    const attrs = formatMarkdownAttributes(CardMarkdownAttrsSchema, {
      ...card,
      color: titleAttrs.color,
    })

    return md`
    ## ${heading} ${attrs}

    ${childrenWithLineBreaks(helpers, blocks)}
  `
  },
  // parseMarkdown happens in card-stack-markdown.ts instead of here.
})

export const UntitledCardContentExtension = Node.create({
  name: "untitledCard",
  group: "card addTarget",
  content: CARD_VALUE_SCHEMA,
  draggable: true,
  addAttributes: () => toTiptapAttributes(CardAttrsSchema, { id: CARD_ID_ATTRIBUTE }),
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { "data-content-type": "untitledCard" }),
    0,
  ],
  parseHTML: () => [{ tag: 'div[data-content-type="untitledCard"]' }],

  renderMarkdown(node: JSONContent, helpers: MarkdownRendererHelpers): string {
    const card = CardAttrsSchema.parse(node.attrs)
    const attrs = formatMarkdownAttributes(CardMarkdownAttrsSchema, { ...card, isUntitled: true })

    return md`
    ## ${attrs}

    ${childrenWithLineBreaks(helpers, node.content || [])}
  `
  },
  // parseMarkdown happens in card-stack-markdown.ts instead of here.
})
